import { Role } from 'auth';
import classNames from 'classnames';
import { get } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useRegisterPermissionsQuery } from '@/modules/common/queries/permissions.ts';
import { ShareholderDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import styles from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderNavigation.module.css';
import { OnboardingHolderNavigation } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderNavigation.tsx';
import { addedShareholdersRecursive } from '@/modules/issuer-dashboard/components/holder-create/generateStepsMappedByFieldHolder.ts';
import { allRoadsHolderByType } from '@/modules/issuer-dashboard/components/holder-create/roadsHolder.ts';
import { OnboardingRoadItem } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import { HolderDetails } from '@/modules/issuer-dashboard/components/holder-create/tabs/HolderDetailsTab.tsx';
import { SelectEntityTypeTab } from '@/modules/issuer-dashboard/components/holder-create/tabs/SelectEntityTypeTab.tsx';
import { WhatCountryTab } from '@/modules/issuer-dashboard/components/holder-create/tabs/WhatCountryTab.tsx';
import {
  HOLDERS_TAB_NAME,
  HoldersTabNames,
} from '@/modules/issuer-dashboard/pages/IssuerHolders.tsx';
import { useIssuerGetInvestorEditByIdQuery } from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { PageLayout, Skeleton, Spinner } from 'ui';
import { ArrowLeftIcon } from 'ui/icons';
import { InvestorEditsStatus } from 'api';

const IssuerHolderUpdate = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [params, setParams] = useSearchParams();
  const { id = 0 } = useParams();

  const { data: dataHolder, isLoading } = useIssuerGetInvestorEditByIdQuery(
    +id,
    id === 0 || isNaN(+id),
  );

  const isTitleUpdate = useMemo(
    () =>
      state?.action === 'update' ||
      !!dataHolder?.data?.old_data?.shareholder_id ||
      !!dataHolder?.data?.new_data?.entity_data?.document_files?.length,
    [dataHolder?.data, state?.action],
  );

  const title = (
    <div className="flex gap-3 items-center">
      <button
        onClick={() => {
          if (!state?.from) {
            params.set(HOLDERS_TAB_NAME, HoldersTabNames.EditRequests);
            params.set('step', '0');
            setParams(params, { replace: false });
          }
          navigate(
            state?.from || {
              pathname: issuerDashboardPaths.static.holders,
              search: params.toString(),
            },
            {
              replace: false,
              state: {
                from: state?.from,
                from2: state?.from2,
              },
            },
          );
        }}
      >
        <img src={ArrowLeftIcon} alt="back" className="w-6 h-6 min-w-6 min-h-6" />
      </button>
      {isLoading && id !== 0 && !isNaN(+id) ? (
        <>
          <Skeleton width={100} height={32} />
          <Skeleton width={60} height={32} />
        </>
      ) : (
        <>
          <span className="text-2xl">
            {isTitleUpdate ? 'Edit holder information' : 'Create new holder'}
          </span>
        </>
      )}
    </div>
  );

  return (
    <PageLayout title={title} contentClassName="py-0 px-0">
      <HolderUpdateBody />
    </PageLayout>
  );
};

const HolderUpdateBody = () => {
  // const [isDirty, setIsDirty] = useState(false);
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const step = params.get('step') ?? '0';
  const { id = 0 } = useParams();
  // const { setBlock } = useBlockerState();

  const { data: dataRegisterPermissions, isLoading: isLoadingRegisterPermissions } =
    useRegisterPermissionsQuery();
  const {
    data: dataHolder,
    isLoading,
    isError,
  } = useIssuerGetInvestorEditByIdQuery(+id, id === 0 || isNaN(+id));
  const roadsTypes = useMemo<OnboardingRoadItem[]>(() => {
    if (!dataHolder?.data?.new_data?.entity_data?.type) return [];

    let items = allRoadsHolderByType?.[dataHolder?.data?.new_data?.entity_data?.type];

    items = items?.filter(item => {
      if (item.entity_type_prefix && item.person_details_key) {
        const shareholders = (
          get(
            dataHolder?.data?.new_data?.entity_data,
            `${item.entity_type_prefix}.${item.person_details_key}`,
            [],
          ) as ShareholderDetails[]
        )?.filter(item => !item.same_as_index);

        if (
          item?.optionalStepsProps?.field_name &&
          item?.optionalStepsProps?.field_name !== item.person_details_key
        ) {
          const subShareholders = shareholders
            .map((subShareholder: ShareholderDetails) =>
              get(subShareholder, item?.optionalStepsProps?.field_name ?? '', [])?.map(
                value => value,
              ),
            )
            .flat() as ShareholderDetails[];

          return !!subShareholders.length;
        }

        return !!shareholders?.length;
      }
      return true;
    });
    const entity_type_prefix = items?.[0]?.entity_type_prefix ?? '';
    //Add trustee Recursive
    const newTrusteeItems: OnboardingRoadItem[] = [];
    const trustee: ShareholderDetails[] =
      dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix]?.trustee || [];
    addedShareholdersRecursive({
      shareholders: trustee,
      newRoadItems: newTrusteeItems,
      entity_type_prefix: entity_type_prefix + '.trustee',
      navName: 'Trustee',
    });
    const trusteeIndex = items.findIndex(item => item.person_details_key === 'trustee');

    if (trusteeIndex !== -1 && newTrusteeItems.length > 0) {
      items.splice(trusteeIndex + 1, 0, ...newTrusteeItems);
    }

    //Add beneficiaries Recursive
    const newBeneficiariesItems: OnboardingRoadItem[] = [];
    const beneficiaries: ShareholderDetails[] =
      dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix]?.beneficiaries || [];
    addedShareholdersRecursive({
      shareholders: beneficiaries,
      newRoadItems: newBeneficiariesItems,
      entity_type_prefix: entity_type_prefix + '.beneficiaries',
      navName: 'Beneficiary',
    });
    const beneficiaryIndex = items.findIndex(item => item.person_details_key === 'beneficiaries');

    if (beneficiaryIndex !== -1 && newBeneficiariesItems.length > 0) {
      items.splice(beneficiaryIndex + 1, 0, ...newBeneficiariesItems);
    }

    //Add settlors Recursive
    const newSettlorsItems: OnboardingRoadItem[] = [];
    const settlors: ShareholderDetails[] =
      dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix]?.settlors || [];
    addedShareholdersRecursive({
      shareholders: settlors,
      newRoadItems: newSettlorsItems,
      entity_type_prefix: entity_type_prefix + '.settlors',
      navName: 'Settlor',
    });
    const settlorIndex = items.findIndex(item => item.person_details_key === 'settlors');

    if (settlorIndex !== -1 && newSettlorsItems.length > 0) {
      items.splice(settlorIndex + 1, 0, ...newSettlorsItems);
    }

    //Add shareholders Recursive
    const newShareholdersItems: OnboardingRoadItem[] = [];
    const shareholders: ShareholderDetails[] =
      dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix]?.shareholders || [];
    addedShareholdersRecursive({
      shareholders: shareholders,
      newRoadItems: newShareholdersItems,
      entity_type_prefix: entity_type_prefix + '.shareholders',
      navName: 'Shareholder',
    });
    const shareholderIndex = items.findIndex(item => item.person_details_key === 'shareholders');

    if (shareholderIndex !== -1 && newShareholdersItems.length > 0) {
      items.splice(shareholderIndex + 1, 0, ...newShareholdersItems);
    }

    return items;
  }, [dataHolder?.data?.new_data?.entity_data]);

  const roads = useMemo<OnboardingRoadItem[]>(() => {
    const firstSteps: OnboardingRoadItem[] = [
      {
        component: HolderDetails,
        navName: 'New holder',
        entity_type_prefix: '',
        getNavProgress: ({ user_data, shareholder_id }) => {
          const completed = !!user_data || !!shareholder_id;
          return {
            percent: 100,
            completed,
          };
        },
      },
      {
        component: WhatCountryTab,
        navName: 'What country is your entity based?',
        entity_type_prefix: '',
        getNavProgress: ({ data }) => {
          const completed =
            (!!data?.country || !!data?.individual_entity_details?.country) && !!data?.type;
          return {
            percent: 100,
            completed,
          };
        },
      },
      {
        component: SelectEntityTypeTab,
        navName: 'What entity type are you investing with?',
        entity_type_prefix: '',
        getNavProgress: ({ data }) => {
          const completed = !!data?.type;
          return {
            percent: 100,
            completed,
          };
        },
      },
    ];

    return [...firstSteps, ...roadsTypes];
  }, [roadsTypes]);

  const {
    label,
    title,
    allowSkip,
    optionalStepsProps,
    entity_type_prefix,
    person_details_key,
    component: Component,
    conditionCallback,
    navName,
  } = roads?.[step && !isNaN(+step) ? +step : 0] ?? {};

  const getSubStep = useCallback((step: number) => +(params.get(`sub_step${step}`) ?? 0), [params]);
  const delSubStep = useCallback((step: number) => params.delete(`sub_step${step}`), [params]);
  const delSubStepRoad = useCallback(
    (step: number, subStep: number) => params.delete(`sub_step_road${step}_${subStep}`),
    [params],
  );

  const handleNextStep = useCallback(() => {
    const currentStep = +step && !isNaN(+step) ? +step : 0;
    const newStep = currentStep + 1;
    params.set('step', String(newStep));
    delSubStep(newStep);
    delSubStepRoad(newStep, 0);
    const subStep = getSubStep(currentStep);
    delSubStepRoad(currentStep, subStep);
    delSubStep(currentStep);
    setParams(params, {
      state: { from: state?.from, from2: state?.from2, action: state?.action },
      replace: false,
    });
  }, [delSubStep, delSubStepRoad, getSubStep, params, setParams, state, step]);

  if (
    (!isLoading && isError) ||
    (!dataRegisterPermissions?.holder_details_permission && !isLoadingRegisterPermissions) ||
    dataHolder?.data?.status === InvestorEditsStatus.Approved
  ) {
    return (
      <Navigate to={{ pathname: issuerDashboardPaths.static.holders, search: params.toString() }} />
    );
  }

  return (
    <div className="w-full h-full flex justify-start items-start">
      {!!Component && (!isLoading || id === 0) && (
        <div className="w-[375px] text-white hidden lg:block h-full">
          <div className="flex gap-3 flex-col items-center justify-start py-6 pl-2 pr-4 h-full w-full relative">
            {/* sidebar */}
            <OnboardingHolderNavigation roads={roads} />
          </div>
        </div>
      )}
      <div className="w-full flex flex-col justify-start items-center">
        <div
          className={classNames(
            'w-full flex flex-col items-center max-h-full h-full gap-3 overflow-y-auto justify-start',
            'font-[Inter] text-[16px] leading-6 px-4 py-4 max-w-[700px] xl:max-w-[900px] 2xl:max-w-[1200px]',
            styles.scroll,
          )}
        >
          {isLoading && id !== 0 ? (
            <div className="w-full h-[300px] flex flex-col justify-center items-center">
              <Spinner size="lg" />
            </div>
          ) : !Component ? (
            <div className="text-lg text-gray-600 font-bold">
              The component was not found for this step :(
            </div>
          ) : (
            <Component
              moveForward={handleNextStep}
              allowSkip={allowSkip}
              options={optionalStepsProps?.options || []}
              subroad={optionalStepsProps?.subroad || []}
              title={optionalStepsProps?.title || title || ''}
              entity_type_prefix={entity_type_prefix || ''}
              field_name={optionalStepsProps?.field_name || ''}
              index={optionalStepsProps?.index}
              description={optionalStepsProps?.description}
              person_details_key={person_details_key}
              conditionCallback={conditionCallback}
              label={label}
              navName={navName}
              roads={roads}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default withPrivateAccess(IssuerHolderUpdate, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
